import { captureException } from '@sentry/react'
import { ASPECT_RATIO_OPTIONS } from 'consts'
import { ErrorKeys } from 'consts/errorKeys'
import { TAppDispatch, TGetState } from 'store/types'
import { IUploadMediaItem } from 'types/media'
import { ReferenceMediaImageType } from 'types/aiVideo'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { withAppThunks } from 'store/with-app-thunks'
import { IMediaFile } from 'store/features/projectData/thunks/media'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { handleApiError } from 'store/utils'
import { createReferenceMediaItem, findMatchingAspectRatio } from '../utils'
import {
  ASPECT_RATIO_PER_TYPE,
  ORIENTATION_BY_ASPECT_RATIO
} from 'containers/SubSidebar/AiImageContent/consts'
import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'

const syncProjectAspectRatioToFrame = (nativeRatio: string | undefined) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const orientation = nativeRatio
      ? ORIENTATION_BY_ASPECT_RATIO[nativeRatio]
      : undefined
    if (!orientation) return

    const {
      width: oldWidth,
      height: oldHeight,
      aspectRatioString
    } = projectDataSelectors.defaultDimensions(getState())

    if (aspectRatioString === ASPECT_RATIO_PER_TYPE[orientation]) return

    const dims = ASPECT_RATIO_OPTIONS[orientation]
    dispatch(
      appThunks.projectDataThunks.setDefaultDimensions({
        width: dims.width,
        height: dims.height
      })
    )
    requestAnimationFrame(() => {
      dispatch(
        appThunks.projectDataThunks.refitAllMediaToCanvas({
          width: oldWidth,
          height: oldHeight
        })
      )
    })
  })

const uploadFrameImage = (
  type: ReferenceMediaImageType,
  uploadItems: IUploadMediaItem[]
) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    const placeholderItem = createReferenceMediaItem(type, '', true)
    try {
      dispatch(
        appThunks.aiVideoThunks.addReferenceMediaItem({
          item: placeholderItem,
          bucket: ReferenceMode.FRAMES
        })
      )

      const uploadedMedias = (await dispatch(
        appThunks.projectDataThunks.uploadMedias(uploadItems)
      )) as IMediaFile[]

      if (uploadedMedias?.length > 0) {
        const uploadedMedia = uploadedMedias[0]
        const dimensions = {
          width: uploadedMedia.width,
          height: uploadedMedia.height
        }
        const nativeRatio = findMatchingAspectRatio(dimensions)

        dispatch(
          appThunks.aiVideoThunks.updateReferenceMediaItem({
            id: placeholderItem.id,
            updates: {
              url: uploadedMedia.filePath,
              isLoading: false,
              dimensions,
              nativeRatio
            },
            bucket: ReferenceMode.FRAMES
          })
        )
        dispatch(appThunks.aiVideoThunks.applyDefaultImageAspectRatio())
        dispatch(
          appThunks.aiVideoThunks.syncProjectAspectRatioToFrame(nativeRatio)
        )
      } else {
        dispatch(
          appThunks.aiVideoThunks.removeReferenceMediaItem({
            id: placeholderItem.id,
            bucket: ReferenceMode.FRAMES
          })
        )
      }

      dispatch(
        appThunks.recentlyUsedMediaThunks.updateRecentlyUsedItemsOnUpload(
          uploadedMedias
        )
      )
    } catch (error) {
      dispatch(
        appThunks.aiVideoThunks.removeReferenceMediaItem({
          id: placeholderItem.id,
          bucket: ReferenceMode.FRAMES
        })
      )
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }
  })

const uploadMultiRefImage = (uploadItems: IUploadMediaItem[]) =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    const placeholderItem = createReferenceMediaItem(
      ReferenceMediaImageType.REFERENCE_IMAGE,
      '',
      true
    )
    try {
      dispatch(
        appThunks.aiVideoThunks.addReferenceMediaItem({
          item: placeholderItem,
          bucket: ReferenceMode.IMAGES
        })
      )

      const uploadedMedias = (await dispatch(
        appThunks.projectDataThunks.uploadMedias(uploadItems)
      )) as IMediaFile[]

      if (uploadedMedias?.length > 0) {
        const uploadedMedia = uploadedMedias[0]
        dispatch(
          appThunks.aiVideoThunks.updateReferenceMediaItem({
            id: placeholderItem.id,
            updates: { url: uploadedMedia.filePath, isLoading: false },
            bucket: ReferenceMode.IMAGES
          })
        )
      } else {
        dispatch(
          appThunks.aiVideoThunks.removeReferenceMediaItem({
            id: placeholderItem.id,
            bucket: ReferenceMode.IMAGES
          })
        )
      }

      dispatch(
        appThunks.recentlyUsedMediaThunks.updateRecentlyUsedItemsOnUpload(
          uploadedMedias
        )
      )
    } catch (error) {
      dispatch(
        appThunks.aiVideoThunks.removeReferenceMediaItem({
          id: placeholderItem.id,
          bucket: ReferenceMode.IMAGES
        })
      )
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }
  })

export const uploadThunks = {
  uploadFrameImage,
  uploadMultiRefImage,
  syncProjectAspectRatioToFrame
}
